import type { Stripe } from "@stripe/stripe-js";

export type CardCheckoutAuthorization = {
  sellerId: number;
  orderNumber: string;
  paymentIntentId: string;
  clientSecret: string;
};

export type PrepareCardCheckoutPaymentResult = {
  cardPaymentMethodId: string;
  authorizations: CardCheckoutAuthorization[];
};

type PrepareCardCheckoutPaymentMutation = (options: {
  variables: {
    data: unknown[];
    oneOffPaymentMethodId?: string | null;
    savedPaymentMethodType?: "ACH" | "CARD" | null;
  };
}) => Promise<{
  data?: {
    prepareCardCheckoutPayment?: PrepareCardCheckoutPaymentResult | null;
  };
}>;

export async function confirmCardCheckoutAuthorizations(
  stripe: Stripe,
  paymentMethodId: string,
  authorizations: CardCheckoutAuthorization[]
): Promise<string[]> {
  const confirmedIds: string[] = [];

  try {
    for (let index = 0; index < authorizations.length; index += 1) {
      const authorization = authorizations[index];
      const { paymentIntent, error } = await stripe.confirmCardPayment(
        authorization.clientSecret,
        {
          payment_method: paymentMethodId,
          return_url: window.location.href,
        }
      );

      if (error) {
        throw new Error(error.message || "Card verification failed.");
      }

      if (!paymentIntent || paymentIntent.status !== "requires_capture") {
        throw new Error(
          "Card authorization did not complete. Please try again."
        );
      }

      if (paymentIntent.id !== authorization.paymentIntentId) {
        throw new Error("Card authorization mismatch. Please try again.");
      }

      confirmedIds.push(paymentIntent.id);
    }
  } catch (error) {
    throw error;
  }

  return confirmedIds;
}

export async function prepareAndConfirmCardCheckout(params: {
  stripe: Stripe;
  orderData: unknown[];
  oneOffPaymentMethodId?: string | null;
  paymentMethodId?: string | null;
  savedPaymentMethodType?: "ACH" | "CARD" | null;
  prepareCardCheckoutPayment: PrepareCardCheckoutPaymentMutation;
}): Promise<{
  cardPaymentIntentIds: string[];
  oneOffPaymentMethodId?: string | null;
}> {
  const { stripe, orderData, prepareCardCheckoutPayment } = params;
  const oneOffPaymentMethodId = params.oneOffPaymentMethodId?.trim() || null;

  const prepareResult = await prepareCardCheckoutPayment({
    variables: {
      data: orderData,
      oneOffPaymentMethodId,
      savedPaymentMethodType: params.savedPaymentMethodType ?? "CARD",
    },
  });

  const prepared = prepareResult.data?.prepareCardCheckoutPayment;
  if (!prepared?.authorizations?.length) {
    throw new Error("Could not initialize card payment. Please try again.");
  }

  const paymentMethodId =
    params.paymentMethodId?.trim() || prepared.cardPaymentMethodId;
  if (!paymentMethodId) {
    throw new Error("No card payment method is available for this checkout.");
  }

  const cardPaymentIntentIds = await confirmCardCheckoutAuthorizations(
    stripe,
    paymentMethodId,
    prepared.authorizations
  );

  return {
    cardPaymentIntentIds,
    oneOffPaymentMethodId,
  };
}
